import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, finalize, map, Observable, of, shareReplay, switchMap, tap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { GlobalConstants } from '../common/global';
import { UserProfile, UserProfileResponse } from '../common/interfaces';

@Injectable({
  providedIn: 'root'
})
export class GetUserProfileService {
  private connectionUserProfile$!: Observable<any>;
  userProfileType = {
    name: '',
    userProfileTypeList: []
  }

  private readonly userProfileSubject = new BehaviorSubject<UserProfile | null>(null);
  public readonly userProfile$ = this.userProfileSubject.asObservable();
  private loading$?: Observable<UserProfile>;

  constructor(private readonly http: HttpClient) { }

  getUserProfile(forceReload: boolean = false): Observable<UserProfile> {
    const cachedData = this.userProfileSubject.getValue();
    if (cachedData && !forceReload) {
      return of(cachedData);
    }

    if (!forceReload && this.loading$) {
      return this.loading$;
    }

    this.loading$ = this.http.get<UserProfileResponse>(`${environment.apiUrl}/v1/main/user/profile`, { headers: GlobalConstants.headers.headers }).pipe(
      map(response => {
        if (!response.success) {
          throwError(() => new Error());
        }
        return response.data;
      }),
      tap((profile) => {
        this.userProfileSubject.next(profile)
      }),
      finalize(() => this.loading$ = undefined),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    return this.loading$;
  }

  updateUserProfile(updatedUserProfile: Partial<UserProfile>): Observable<UserProfile> {
    return this.http.patch<{ success: boolean }>(`${environment.apiUrl}/v1/main/user/profile?connId=${environment.connId}`, updatedUserProfile, { headers: GlobalConstants.headers.headers }).pipe(
      map(response => {
        if (!response.success) {
          throwError(() => new Error());
        }
        return response;
      }),
      switchMap(() => this.getUserProfile(true))
    );
  }

  getConnectionUserProfile(forceReload: boolean = false): Observable<any> {
    if (!this.connectionUserProfile$ || forceReload) {
      this.connectionUserProfile$ = this.http
        .get<any>(
          `${environment.apiUrl}/conn/${environment.connId}/v1/user/profile`,
          GlobalConstants.headersCentral
        )
        .pipe(
          shareReplay(1)
        );
    }

    return this.connectionUserProfile$;
  }

  getProfileType(profileType: string): Observable<any> {
    if(this.userProfileType.name === profileType) {
      return of(this.userProfileType.userProfileTypeList);
    } else {
      return this.http.get(`${environment.apiUrl}/conn/${environment.connId}/v1/profiles/types/${profileType}`, GlobalConstants.headers).pipe(
        tap((response: any) => {
          this.userProfileType.name = profileType;
          this.userProfileType.userProfileTypeList = response;
        })
      );
    }
  }
}